// Iter40 — Sorting Result Detail Drawer
//
// Slide-in from the right within /sorting. Shows full result data + attached
// photos thumbnails + linked incident link + audit timeline for the result.
// Reuses the photo download via the existing /api/incidents/{id}/attachments/{fname}.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { DefectBreakdownModal } from "./DefectBreakdownModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rateColor = (r) => (r > 10 ? "#E24B4A" : r > 5 ? "#f3b14a" : "#4ed084");

export const SortingResultDetailDrawer = ({ resultId, onClose }) => {
  const [result, setResult] = useState(null);
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Pull all visible results & filter by id — avoids extra fetch route
        const { data: list } = await axios.get(`${API}/sorting/my-results?limit=500`);
        const r = list.find((x) => x.id === resultId);
        if (!cancelled) setResult(r || null);
        if (r) {
          try {
            const { data: inc } = await axios.get(`${API}/incidents/${r.incident_id}`);
            if (!cancelled) setIncident(inc);
          } catch (_) { /* tolerated — restricted users may not have incident GET */ }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [resultId]);

  const downloadPdf = async () => {
    if (!result) return;
    try {
      const resp = await axios.get(`${API}/sorting-results/${result.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${result.id}-summary.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) { /* swallow */ }
  };

  const findAttachment = (id) => {
    if (!incident) return null;
    return (incident.attachments || []).find((a) => a.id === id) || null;
  };

  return (
    <div
      className="fixed inset-0 flex justify-end"
      style={{ background: "rgba(0,0,0,0.55)", zIndex: 400 }}
      onClick={onClose}
      data-testid="sorting-result-detail-drawer"
    >
      <div
        className="h-full overflow-y-auto"
        style={{
          width: "min(560px, 96vw)",
          background: "var(--bg2, #0d0f18)",
          borderLeft: "1px solid rgba(255,255,255,0.14)",
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
              Sorting Result
            </span>
            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9ec3ec" }} data-testid="sorting-drawer-id">
              {resultId}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none", padding: 0 }}
            data-testid="sorting-drawer-close"
          >×</button>
        </div>

        {loading && (
          <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#6b7388" }} data-testid="sorting-drawer-loading">
            Loading…
          </div>
        )}

        {!loading && !result && (
          <div className="rounded p-4 text-center" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.30)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="sorting-drawer-missing">
            Result not found or access denied.
          </div>
        )}

        {result && (
          <>
            <div
              className="rounded-md p-3 mb-4"
              style={{
                background: "var(--bg-surface, #111420)",
                border: "1px solid rgba(255,255,255,0.10)",
                borderLeft: `3px solid ${rateColor(result.overall_defect_rate_pct ?? result.defect_rate_pct ?? 0)}`,
              }}
              data-testid="sorting-drawer-summary"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.6px", textTransform: "uppercase" }}>
                  Overall defect rate · {(result.entries || []).length} entry/entries
                </span>
                <span style={{ fontFamily: "DM Mono, monospace", fontSize: 24, fontWeight: 700, color: rateColor(result.overall_defect_rate_pct ?? result.defect_rate_pct ?? 0) }} data-testid="sorting-drawer-rate">
                  {(result.overall_defect_rate_pct ?? result.defect_rate_pct ?? 0).toFixed(1)}%
                </span>
              </div>
              <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#c5cad8" }}>
                {result.incident_id} · {result.incident_title || ""}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="sorting-drawer-qty">
              <QtyCell label="TOTAL" value={result.grand_total_qty ?? ((result.total_ok ?? 0) + (result.total_nok ?? 0) + (result.total_rework ?? 0))} color="#9ec3ec" />
              <QtyCell label="OK" value={result.total_ok ?? result.ok_qty ?? 0} color="#4ed084" />
              <QtyCell label="NOK" value={result.total_nok ?? result.nok_qty ?? 0} color="#E24B4A" />
              <QtyCell label="REWORK" value={result.total_rework ?? result.rework_qty ?? 0} color="#f3b14a" />
            </div>

            <BatchTable entries={result.entries || []} resultId={result.id} onResultUpdated={setResult} />

            <Field label="Location">{result.sorting_location}</Field>
            <Field label="Sorter count">{result.sorter_count}</Field>
            <Field label="Start">{result.start_datetime || "—"}</Field>
            <Field label="End">{result.end_datetime || "—"}</Field>
            <Field label="Submitted">{result.created_at ? new Date(result.created_at).toLocaleString() : "—"}</Field>

            {result.photos && result.photos.length > 0 && (
              <div className="mb-4" data-testid="sorting-drawer-photos">
                <Lbl>Photos ({result.photos.length})</Lbl>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {result.photos.map((aid) => {
                    const att = findAttachment(aid);
                    if (!att) {
                      return (
                        <div key={aid}
                          className="rounded p-2 text-center"
                          style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "DM Mono, monospace", fontSize: 9, color: "#6b7388" }}
                          data-testid={`sorting-drawer-photo-stub-${aid}`}
                        >
                          📎 {aid.slice(0, 8)}
                        </div>
                      );
                    }
                    const isImg = (att.content_type || "").startsWith("image/");
                    const url = `${API}/incidents/${result.incident_id}/attachments/${att.stored_name}`;
                    return (
                      <a key={aid} href={url} target="_blank" rel="noopener noreferrer"
                        className="block rounded overflow-hidden"
                        style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.10)", aspectRatio: "1 / 1" }}
                        data-testid={`sorting-drawer-photo-${aid}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isImg ? (
                          <img src={url} alt={att.original_name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ padding: 8, fontFamily: "DM Mono, monospace", fontSize: 9, color: "#c5cad8" }}>{att.original_name}</div>
                        )}
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {result.report_pdf && (
              <div className="mb-4" data-testid="sorting-drawer-report-pdf">
                <Lbl>Report PDF</Lbl>
                {(() => {
                  const att = findAttachment(result.report_pdf);
                  if (!att) return <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#6b7388", marginTop: 4 }}>📎 {result.report_pdf.slice(0, 12)}</div>;
                  const url = `${API}/incidents/${result.incident_id}/attachments/${att.stored_name}`;
                  return (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="inline-block mt-1 px-3 py-1.5 rounded"
                      style={{ background: "rgba(155,89,182,0.12)", border: "1px solid rgba(155,89,182,0.40)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 10, textDecoration: "none" }}>
                      📄 {att.original_name}
                    </a>
                  );
                })()}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={downloadPdf}
                className="px-3 py-2 rounded-md"
                style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                data-testid="sorting-drawer-pdf-btn"
              >
                📥 Download summary PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-1.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
    <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.5px", textTransform: "uppercase" }}>
      {label}
    </span>
    <span style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "var(--text, #f0f2f8)" }}>
      {children}
    </span>
  </div>
);

const Lbl = ({ children }) => (
  <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.8px", textTransform: "uppercase" }}>{children}</span>
);

const QtyCell = ({ label, value, color }) => (
  <div className="rounded p-2 text-center" style={{ background: `${color}10`, border: `1px solid ${color}40` }}>
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color, letterSpacing: "0.8px" }}>{label}</div>
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 16, fontWeight: 700, color: "var(--text, #f0f2f8)" }}>
      {(value || 0).toLocaleString()}
    </div>
  </div>
);

// Iter41 — Batch entries table. Groups by batch_number with per-batch subtotal,
// while still showing each (batch × inspector) row.
// Iter79 — adds an "Edit defects" button per entry row that opens
// DefectBreakdownModal and PATCHes /api/sorting-results/{id} with entry_defects.
const BatchTable = ({ entries, resultId, onResultUpdated }) => {
  const [editing, setEditing] = useState(null); // {batch_number, defects, nokQty, reworkQty, rowLabel}
  const [savingErr, setSavingErr] = useState("");

  if (!entries || entries.length === 0) return null;
  // Group by batch_number for display, preserving entry order
  const groups = [];
  const groupIdx = new Map();
  for (const e of entries) {
    const b = e.batch_number || "—";
    let idx = groupIdx.get(b);
    if (idx === undefined) {
      idx = groups.length;
      groupIdx.set(b, idx);
      groups.push({ batch: b, rows: [] });
    }
    groups[idx].rows.push(e);
  }
  const allDefectTypes = Array.from(new Set(entries.flatMap((e) => Object.keys(e.defect_breakdown || {})))); // kept for backward compat
  void allDefectTypes;
  return (
    <div className="mb-4" data-testid="sorting-drawer-batches">
      <div className="flex items-center justify-between mb-1.5">
        <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", letterSpacing: "0.8px", textTransform: "uppercase" }}>
          Entries ({entries.length}) · {groups.length} batch(es)
        </span>
      </div>
      <div className="rounded overflow-x-auto" style={{ background: "var(--bg-surface, #111420)", border: "1px solid rgba(255,255,255,0.10)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.04)" }}>
              <BTh>Inspector</BTh>
              <BTh align="right">Total</BTh>
              <BTh align="right">OK</BTh>
              <BTh align="right">NOK</BTh>
              <BTh align="right">REW</BTh>
              <BTh align="right">Rate %</BTh>
              <BTh>Defects (qty)</BTh>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => {
              const t_ok = g.rows.reduce((s, r) => s + (r.ok_qty || 0), 0);
              const t_nok = g.rows.reduce((s, r) => s + (r.nok_qty || 0), 0);
              const t_rew = g.rows.reduce((s, r) => s + (r.rework_qty || 0), 0);
              const t_total = g.rows.reduce(
                (s, r) => s + (r.total_qty != null ? r.total_qty : ((r.ok_qty || 0) + (r.nok_qty || 0) + (r.rework_qty || 0))),
                0,
              );
              const t_rate = t_total > 0 ? (t_nok / t_total) * 100 : 0;
              return (
                <React.Fragment key={`${g.batch}-${gi}`}>
                  <tr style={{ background: "rgba(24,95,165,0.06)" }} data-testid={`sorting-drawer-batch-${g.batch}`}>
                    <BTd colSpan={7}>
                      <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec", letterSpacing: "0.6px" }}>
                        BATCH · {g.batch} · {g.rows.length} inspector(s)
                      </span>
                    </BTd>
                  </tr>
                  {g.rows.map((r, ri) => {
                    const tot = r.total_qty != null ? r.total_qty : ((r.ok_qty || 0) + (r.nok_qty || 0) + (r.rework_qty || 0));
                    const rate = tot > 0 ? ((r.nok_qty || 0) / tot) * 100 : 0;
                    const bdEntries = Object.entries(r.defect_breakdown || {});
                    return (
                      <tr key={ri} data-testid={`sorting-drawer-entry-${gi}-${ri}`} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <BTd>{r.inspector_name || "—"}</BTd>
                        <BTd align="right" color="#9ec3ec" bold>{tot.toLocaleString()}</BTd>
                        <BTd align="right" color="#4ed084">{(r.ok_qty || 0).toLocaleString()}</BTd>
                        <BTd align="right" color="#E24B4A">{(r.nok_qty || 0).toLocaleString()}</BTd>
                        <BTd align="right" color="#f3b14a">{(r.rework_qty || 0).toLocaleString()}</BTd>
                        <BTd align="right" color={rateColor(rate)} bold>{rate.toFixed(1)}%</BTd>
                        <BTd>
                          <div className="flex items-center gap-2">
                            <span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#c89adb" }} data-testid={`sorting-drawer-defects-text-${gi}-${ri}`}>
                              {bdEntries.length ? bdEntries.map(([k, v]) => `${k}:${v}`).join(", ") : "—"}
                            </span>
                            {resultId && (
                              <button
                                type="button"
                                onClick={() => setEditing({
                                  batch_number: r.batch_number || g.batch,
                                  defects: r.defect_breakdown || {},
                                  nokQty: r.nok_qty || 0,
                                  reworkQty: r.rework_qty || 0,
                                  rowLabel: `${g.batch} · ${r.inspector_name || "—"}`,
                                })}
                                className="px-1.5 py-0.5 rounded"
                                style={{ background: "rgba(155,89,182,0.16)", border: "1px solid rgba(155,89,182,0.45)", color: "#c89adb", fontFamily: "DM Mono, monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.3px", cursor: "pointer" }}
                                data-testid={`sorting-drawer-edit-defects-${gi}-${ri}`}
                                title="Edit defect breakdown for this row"
                              >
                                ✎ EDIT
                              </button>
                            )}
                          </div>
                        </BTd>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.02)" }} data-testid={`sorting-drawer-batch-subtotal-${g.batch}`}>
                    <BTd><span style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.5px" }}>SUBTOTAL</span></BTd>
                    <BTd align="right" bold color="#9ec3ec">{t_total.toLocaleString()}</BTd>
                    <BTd align="right" bold color="#4ed084">{t_ok.toLocaleString()}</BTd>
                    <BTd align="right" bold color="#E24B4A">{t_nok.toLocaleString()}</BTd>
                    <BTd align="right" bold color="#f3b14a">{t_rew.toLocaleString()}</BTd>
                    <BTd align="right" bold color={rateColor(t_rate)}>{t_rate.toFixed(1)}%</BTd>
                    <BTd> </BTd>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {savingErr && (
        <div className="mt-2 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 10 }} data-testid="sorting-drawer-defects-save-error">
          {savingErr}
        </div>
      )}
      {editing && (
        <DefectBreakdownModal
          initial={editing.defects}
          nokQty={editing.nokQty}
          reworkQty={editing.reworkQty}
          rowLabel={editing.rowLabel}
          onClose={() => setEditing(null)}
          onSave={async (map) => {
            setSavingErr("");
            try {
              const { data } = await axios.patch(`${API}/sorting-results/${resultId}`, {
                entry_defects: [{ batch_number: editing.batch_number, defect_breakdown: map }],
              });
              setEditing(null);
              if (onResultUpdated) onResultUpdated(data);
            } catch (e) {
              setSavingErr(e?.response?.data?.detail || e.message || "Save failed");
            }
          }}
        />
      )}
    </div>
  );
};

const BTh = ({ children, align }) => (
  <th style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.8px", textTransform: "uppercase", textAlign: align || "left", padding: "4px 8px" }}>{children}</th>
);
const BTd = ({ children, align, colSpan, color, bold }) => (
  <td colSpan={colSpan} style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: color || "var(--text, #f0f2f8)", fontWeight: bold ? 700 : 400, padding: "4px 8px", textAlign: align || "left" }}>{children}</td>
);
